import { format, parseISO } from "date-fns";

import type {
  DietaryLabel,
  MenuData,
  MenuItem,
} from "@luna-restaurant/feature-luna-school-menu-plan/build/components/MenuCard/types";
import type { DishPlan, DishPlanItem } from "@luna-restaurant/school-api";

export const DISH_PLAN_STALE_TIME_MS = 60 * 60 * 1000;

const mapMealTypeToLabel = (
  mealType?: DishPlanItem["mealType"],
): DietaryLabel => {
  switch (mealType) {
    case "FISH":
      return "fish";
    case "MEAT":
      return "meat";
    default:
      return "vegetarian";
  }
};

const stripHtmlTags = (text: string): string =>
  text.replaceAll("<p>", "").replaceAll("</p>", "");

export const formatDateKey = (date: Date) => format(date, "yyyy-MM-dd");

export type NormalizedPlanItem = {
  "@id": MenuItem["@id"];
  id: MenuItem["id"];
  date: Date;
  dayNumber: number;
  title: string;
  label: DietaryLabel;
  position: number;
  isDessert: boolean;
};

const getItemDate = (item: DishPlanItem): Date | null => {
  if (!item.date) {
    return null;
  }

  const parsed = parseISO(item.date);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const isDessertItem = (type?: DishPlanItem["dishType"]) => type === "DESSERT";

export const getPlanItems = (
  plan: DishPlan | null | undefined,
): DishPlanItem[] => plan?.items ?? [];

export const normalizePlanItem = (
  item: DishPlanItem,
  displayStart: Date,
  displayEnd: Date,
): NormalizedPlanItem | null => {
  const date = getItemDate(item);

  if (!date || date < displayStart || date > displayEnd) {
    return null;
  }

  return {
    "@id": item["@id"] ?? `/v1/dish-plan-items/${item.id}`,
    id: item.id,
    date,
    dayNumber: date.getDate(),
    title: stripHtmlTags(item.title),
    label: mapMealTypeToLabel(item.mealType),
    position: item.position,
    isDessert: isDessertItem(item.dishType),
  };
};

export const groupItemsToMenuData = (
  items: NormalizedPlanItem[],
): MenuData | null => {
  if (items.length === 0) return null;

  const sorted = [...items].sort((a, b) => a.position - b.position);
  const menus: MenuItem[] = [];
  const desserts: string[] = [];

  for (const item of sorted) {
    if (item.isDessert) {
      desserts.push(item.title);
    } else {
      menus.push({
        "@id": item["@id"],
        id: item.id,
        title: item.title,
        label: item.label,
        isSelected: false,
        position: item.position,
      });
    }
  }

  if (menus.length === 0 && desserts.length === 0) return null;

  return {
    dayNumber: items[0].dayNumber,
    month: items[0].date.getMonth(),
    menus,
    desserts,
  };
};
